import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  setSelectedItem,
  setDebouncedSearchQuery,
  resetBoardState,
  clearAllFilters,
  setGroupBy,
  setSubGroupBy,
  setShowGroupOptions,
  setColumns,
  setColumnOrder,
  setLoadingStatus,
  setPagination,
  setHasMore,
  setInitialLoading,
} from "@/features/bughurd/slice";
import {
  selectBoardState,
  selectSearchQuery,
  selectGroupingConfig,
  selectIsGroupingDataReady,
  selectIsSubGroupingDataReady,
  selectGroupingDataMemoized,
  selectSubGroupingDataMemoized,
  selectColumns,
  selectViewType,
  selectApiData,
  selectFilters,
  selectDebouncedSearchQuery,
  selectActiveSprintIds,
} from "@/features/bughurd/boardSelector";
import { useBoardData } from "./useBoardData";
import { useBoardFetch } from "./useBoardFetch";
import { useSubGroupOperations } from "./useSubGroupOperations";

export const useBoardEffects = (
  id?: string,
  selectedItem?: string,
  isMyTaskView?: boolean,
  refetchStatusList?: () => void
) => {
  const dispatch = useAppDispatch();
  const boardState = useAppSelector(selectBoardState);
  const searchQuery = useAppSelector(selectSearchQuery);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const isGroupingDataReady = useAppSelector(selectIsGroupingDataReady);
  const isSubGroupingDataReady = useAppSelector(selectIsSubGroupingDataReady);
  const groupingData = useAppSelector(selectGroupingDataMemoized);
  const subGroupingData = useAppSelector(selectSubGroupingDataMemoized);
  const columns = useAppSelector(selectColumns);
  const viewType = useAppSelector(selectViewType);
  const { fetchBugsForSubGroups } = useSubGroupOperations();
  const apiData = useAppSelector(selectApiData);
  const filters = useAppSelector(selectFilters);
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);
  const { groupBy, subGroupBy, sortOrder } = groupingConfig;
  const { statusList } = apiData;
  const backlogViewType = useAppSelector(
    (state) => state.board.backlogViewType
  );
  const { projectDetails } = boardState;

  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    selectedCreatedBy,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
  } = filters;

  const {
    statusDataInitialized,
    viewChangeRef,
    isGroupByChanging,
    groupByChangeRef,
    PAGE_LIMIT,
    previousTasksRef,
    fetchInProgress,
    skipStatusRefetch,
    isDragInProgress,
    lastFetchParams,
  } = useBoardData();

  const { fetchBugsForGroup } = useBoardFetch();

  const activeSprintIds = useAppSelector(selectActiveSprintIds);

  // Track when we should initialize columns
  const shouldInitializeColumns = useRef<boolean>(false);

  const selectedSprintData = boardState?.selectedSprintData;

  // Helper function to check if fetch parameters have changed
  const haveFetchParamsChanged = (): boolean => {
    const currentParams = {
      search: debouncedSearchQuery,
      tags: selectedTags,
      avatars: selectedAvatars,
      severities: selectedPriorities,
      statuses: selectedStatuses,
      projects: selectedProjects,
      dueDate: selectedDueDate,
      startDate: selectedStartDate,
      updatedDate: selectedUpdatedDate,
      createdBy: selectedCreatedBy,
      dueDateStart,
      dueDateEnd,
      startDateStart,
      startDateEnd,
      updatedAtStart,
      updatedAtEnd,
      groupBy,
      subGroupBy,
      isRecurring,
      activeSprintIds,
      selectedSprintId: selectedSprintData?._id || null,
    };

    const lastParams = lastFetchParams.current;

    const tagsChanged =
      currentParams.tags.length !== lastParams.tags.length ||
      currentParams.tags.some((tag) => !lastParams.tags.includes(tag));

    const avatarsChanged =
      currentParams.avatars.length !== lastParams.avatars.length ||
      currentParams.avatars.some(
        (avatar) => !lastParams.avatars.includes(avatar)
      );

    const createdByChanged =
      currentParams.createdBy.length !== lastParams.createdBy.length ||
      currentParams.createdBy.some(
        (createdBy) => !lastParams.createdBy.includes(createdBy)
      );

    const severitiesChanged =
      currentParams.severities.length !== lastParams.severities.length ||
      currentParams.severities.some(
        (severity) => !lastParams.severities.includes(severity)
      );

    const statusesChanged =
      currentParams.statuses.length !== lastParams.statuses.length ||
      currentParams.statuses.some(
        (status) => !lastParams.statuses.includes(status)
      );

    const projectsChanged =
      currentParams.projects.length !== lastParams.projects.length ||
      currentParams.projects.some(
        (project) => !lastParams.projects.includes(project)
      );

    return (
      currentParams.search !== lastParams.search ||
      tagsChanged ||
      avatarsChanged ||
      severitiesChanged ||
      statusesChanged ||
      projectsChanged ||
      createdByChanged ||
      currentParams.dueDateStart !== lastParams.dueDateStart ||
      currentParams.dueDateEnd !== lastParams.dueDateEnd ||
      currentParams.startDateStart !== lastParams.startDateStart ||
      currentParams.startDateEnd !== lastParams.startDateEnd ||
      currentParams.updatedAtStart !== lastParams.updatedAtStart ||
      currentParams.updatedAtEnd !== lastParams.updatedAtEnd ||
      currentParams.groupBy !== lastParams.groupBy ||
      currentParams.subGroupBy !== lastParams.subGroupBy ||
      currentParams.isRecurring !== lastParams.isRecurring ||
      currentParams.activeSprintIds !== lastParams.activeSprintIds ||
      currentParams.selectedSprintId !== lastParams.selectedSprintId
    );
  };

  const updateLastFetchParams = () => {
    lastFetchParams.current = {
      search: debouncedSearchQuery,
      tags: [...selectedTags],
      avatars: [...selectedAvatars],
      createdBy: [...selectedCreatedBy],
      severities: [...selectedPriorities],
      statuses: [...selectedStatuses],
      projects: [...selectedProjects],
      dueDate: selectedDueDate,
      startDate: selectedStartDate,
      updatedDate: selectedUpdatedDate,
      dueDateStart,
      dueDateEnd,
      startDateStart,
      startDateEnd,
      updatedAtStart,
      updatedAtEnd,
      groupBy,
      subGroupBy,
      isRecurring,
      activeSprintIds,
      selectedSprintId: selectedSprintData?._id || null,
    };
  };

  const shouldFetchData = () => {
    const boardType = projectDetails?.boardType;
    if (selectedItem === "My Tasks") {
      return true;
    }
    if (boardType !== "SCRUM") {
      return selectedItem === "Task Board" || selectedItem === "My Tasks";
    }

    if (boardType === "SCRUM") {
      const isSprintView =
        selectedItem === "All Sprints" ||
        selectedItem?.toLowerCase().includes("sprint");
      const isBacklogView = selectedItem === "Backlog";
      const needsDataFetch =
        backlogViewType === "kanban" ||
        backlogViewType === "list" ||
        backlogViewType === "table";

      return (isSprintView || isBacklogView) && needsDataFetch;
    }

    return false;
  };

  // *** MISSING EFFECT: Fetch data when filter parameters change ***
  useEffect(() => {
    if (
      !shouldFetchData() ||
      !isGroupingDataReady ||
      isDragInProgress.current ||
      isGroupByChanging.current
    ) {
      return;
    }
    const boardType = projectDetails?.boardType;

    const paramsChanged = haveFetchParamsChanged();

    if (paramsChanged || viewChangeRef.current) {
      viewChangeRef.current = false;

      updateLastFetchParams();

      // Clear any ongoing fetches before starting new ones
      Object.keys(fetchInProgress.current).forEach((key) => {
        fetchInProgress.current[key] = false;
      });
      fetchInProgress.current = {};

      if (boardType === "SCRUM" && activeSprintIds.length === 0) {
        return;
      }

      if (subGroupBy === "None") {
        // Single-level grouping
        if (Object.keys(columns).length === 0) {
          return;
        }

        Object.keys(groupingData).forEach((groupId) => {
          dispatch(
            setPagination({
              key: groupId,
              pagination: {
                page: 1,
                limit: PAGE_LIMIT,
              },
            })
          );

          dispatch(
            setHasMore({
              key: groupId,
              hasMore: true,
            })
          );

          previousTasksRef.current[groupId] = 0;
        });

        // Add a small delay and check to ensure we're fetching for the current groupBy
        setTimeout(() => {
          if (groupByChangeRef.current === groupBy) {
            Object.keys(groupingData).forEach((groupId) => {
              const groupData = groupingData[groupId];
              fetchBugsForGroup(groupId, groupData, 1, PAGE_LIMIT, false);
            });
          }
        }, 0);
      } else {
        // Two-level grouping with sub-groups
        if (isSubGroupingDataReady) {
          // Only fetch if not already in progress
          const isAnyFetchInProgress = Object.keys(subGroupingData).some(
            (subGroupId) =>
              Object.keys(groupingData).some(
                (groupId) => fetchInProgress.current[`${subGroupId}-${groupId}`]
              )
          );

          if (!isAnyFetchInProgress && groupByChangeRef.current === groupBy) {
            fetchBugsForSubGroups();
          }
        }
      }

      dispatch(setInitialLoading(false));
    }
  }, [
    // Core conditions
    selectedItem,
    viewType,
    backlogViewType,
    isGroupingDataReady,
    columns,

    // Filter dependencies - These trigger refetch when changed
    debouncedSearchQuery,
    selectedTags,
    selectedAvatars,
    selectedPriorities,
    selectedStatuses,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    selectedCreatedBy,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,

    // Grouping dependencies
    groupBy,
    subGroupBy,
    sortOrder,

    // Data dependencies
    isSubGroupingDataReady,
    groupingData,
    subGroupingData,

    // Functions
    fetchBugsForGroup,
    activeSprintIds,
    selectedSprintData,
  ]);

  //********************************************************** */
  // Effect to set selected item if not set
  useEffect(() => {
    if (!selectedItem || selectedItem === "" || selectedItem === undefined) {
      dispatch(setSelectedItem("Task Board"));
    }
    if (
      selectedItem === "Task Board" &&
      !skipStatusRefetch.current &&
      refetchStatusList
    ) {
      refetchStatusList();
    }
  }, [selectedItem, id, refetchStatusList, dispatch]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setDebouncedSearchQuery(searchQuery));
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery, dispatch]);

  // Track groupBy changes
  useEffect(() => {
    if (groupByChangeRef.current !== groupBy) {
      isGroupByChanging.current = true;
      groupByChangeRef.current = groupBy;

      Object.keys(fetchInProgress.current).forEach((key) => {
        fetchInProgress.current[key] = false;
      });
      fetchInProgress.current = {};
    }
  }, [groupBy]);

  // Initialize columns based on grouping
  useEffect(() => {
    const boardType = projectDetails?.boardType;
    let currentViewType = viewType;
    if (selectedItem !== "My Tasks" && boardType === "SCRUM") {
      currentViewType = backlogViewType;
    }
    const isValidView =
      currentViewType === "kanban" ||
      currentViewType === "list" ||
      currentViewType === "table";
    const isValidSelectedItem =
      selectedItem === "Task Board" ||
      selectedItem === "My Tasks" ||
      (boardType === "SCRUM" &&
        (selectedItem === "All Sprints" ||
          selectedItem?.toLowerCase().includes("sprint") ||
          selectedItem === "Backlog"));
    if (
      isValidView &&
      isValidSelectedItem &&
      isGroupingDataReady &&
      !statusDataInitialized.current &&
      !isDragInProgress.current &&
      Object.keys(groupingData).length > 0
    ) {
      // Skip if groupBy is still changing
      if (isGroupByChanging.current) {
        return;
      }
      const boardType = projectDetails?.boardType;

      // For list and table view, we don't need sub-grouping, only kanban supports it
      if (
        viewType === "list" ||
        viewType === "table" ||
        (viewType === "kanban" && subGroupBy === "None")
      ) {
        // Skip initialization for table view with groupBy === "None"
        if (viewType === "table" && groupBy === "None") {
          return;
        }

        // Wait for the right sprint context before initializing columns.
        // For a specific sprint URL we need selectedSprintData in Redux;
        // for All Sprints / Backlog we need at least one activeSprintId.
        const isSpecificSprint = selectedItem?.startsWith("Sprint:");
        if (!isMyTaskView && boardType === "SCRUM") {
          if (isSpecificSprint && !boardState?.selectedSprintData) return;
          if (!isSpecificSprint && activeSprintIds.length === 0) return;
        }

        // Only mark initialized AFTER the guard so a retry is possible if we returned early above.
        statusDataInitialized.current = true;
        isGroupByChanging.current = false; // Reset the flag

        const newColumnOrder = Object.keys(groupingData);
        dispatch(setColumnOrder(newColumnOrder));

        const emptyColumns = Object.keys(groupingData).reduce(
          (acc, groupId) => {
            const groupData = groupingData[groupId];
            return {
              ...acc,
              [groupId]: {
                id: groupId,
                title: groupData.title,
                color: groupData.color,
                icon: groupData.icon,
                tasks: [],
                totalCount: 0,
                description: groupData.description || "",
                hidden: groupData.hidden || false,
              },
            };
          },
          {}
        );

        dispatch(setColumns(emptyColumns));

        // Set loading statuses and pagination
        Object.keys(groupingData).forEach((groupId) => {
          dispatch(
            setLoadingStatus({
              key: groupId,
              loading: {
                isInitialLoading: true,
                isLoadingMore: false,
              },
            })
          );

          dispatch(
            setPagination({
              key: groupId,
              pagination: {
                page: 1,
                limit: PAGE_LIMIT,
              },
            })
          );

          dispatch(
            setHasMore({
              key: groupId,
              hasMore: true,
            })
          );

          previousTasksRef.current[groupId] = 0;
        });

        // Trigger initial data fetch with a slight delay to ensure state is settled
        setTimeout(() => {
          // Double-check that we're still on the same groupBy
          if (groupByChangeRef.current === groupBy) {
            Object.keys(groupingData).forEach((groupId) => {
              const groupData = groupingData[groupId];
              fetchBugsForGroup(groupId, groupData, 1, PAGE_LIMIT, false);
            });
          }
        }, 50); // Small delay to ensure state consistency
      } else if (viewType === "kanban" && subGroupBy !== "None") {
        // Handle kanban with sub-groups
        statusDataInitialized.current = true;
        isGroupByChanging.current = false; // Reset the flag
        dispatch(setColumns({}));
        dispatch(setColumnOrder([]));

        if (isSubGroupingDataReady && isGroupingDataReady) {
          setTimeout(() => {
            // Double-check that we're still on the same groupBy
            if (groupByChangeRef.current === groupBy) {
              fetchBugsForSubGroups();
            }
          }, 50);
        }
      }
    }
  }, [
    selectedItem,
    viewType,
    backlogViewType,
    isGroupingDataReady,
    isSubGroupingDataReady,
    groupBy,
    subGroupBy,
    fetchBugsForGroup,
    groupingData,
    subGroupingData,
    PAGE_LIMIT,
    activeSprintIds,
    boardState?.selectedSprintData,
  ]);

  // Reset state when view type changes
  useEffect(() => {
    statusDataInitialized.current = false;
    viewChangeRef.current = true;
    isGroupByChanging.current = false; // Reset when view changes
    const boardType = projectDetails?.boardType;
    const currentViewType = boardType === "SCRUM" ? backlogViewType : viewType;
    if (
      currentViewType !== "table" &&
      currentViewType !== "table" &&
      groupBy === "None"
    ) {
      dispatch(setGroupBy("Status"));
    }

    // Clear all ongoing fetches
    Object.keys(fetchInProgress.current).forEach((key) => {
      fetchInProgress.current[key] = false;
    });
    fetchInProgress.current = {};

    // Reset sub-group when view type changes to list or table
    if (currentViewType !== "kanban") {
      dispatch(setSubGroupBy("None"));
    }
  }, [viewType, groupBy, selectedItem, dispatch]);

  // Reset state when ID changes
  useEffect(() => {
    const resetState = () => {
      dispatch(resetBoardState());
      dispatch(setInitialLoading(true));
      statusDataInitialized.current = false;
      viewChangeRef.current = true;
      isGroupByChanging.current = false;
      groupByChangeRef.current = groupBy;

      // Clear all ongoing fetches
      Object.keys(fetchInProgress.current).forEach((key) => {
        fetchInProgress.current[key] = false;
      });
      fetchInProgress.current = {};

      skipStatusRefetch.current = false;
      dispatch(clearAllFilters());
    };
    resetState();
    if (selectedItem === "Task Board") {
      refetchStatusList();
    }
    return () => {
      skipStatusRefetch.current = false;
    };
  }, [id, selectedItem, isMyTaskView, subGroupBy, groupBy, refetchStatusList]);

  // Initialize default grouping
  useEffect(() => {
    dispatch(setGroupBy("Status"));
    dispatch(setShowGroupOptions(false));
    dispatch(setSubGroupBy("None"));
  }, [id, dispatch]);

  // Handle table view data when groupBy is "None"
  useEffect(() => {
    if (
      viewType === "table" &&
      groupBy === "None" &&
      (selectedItem === "Task Board" || selectedItem === "My Tasks")
    ) {
      // For table view without grouping, clear columns structure
      dispatch(setColumns({}));
      dispatch(setColumnOrder([]));
    }
  }, [viewType, groupBy, selectedItem, dispatch]);

  // Handle switching away from "None" grouping
  useEffect(() => {
    if (
      viewType === "table" &&
      groupBy !== "None" &&
      (selectedItem === "Task Board" || selectedItem === "My Tasks")
    ) {
      // Clear the "all-tasks" column when switching to grouped view
      dispatch(setColumns({}));
      dispatch(setColumnOrder([]));
    }
  }, [
    viewType,
    groupBy,
    selectedItem,
    dispatch,
    backlogViewType,
    projectDetails,
  ]);
};
